import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

const Sidebar = ({ setPageTitle, isOpen, onToggle, onClose, isMobile }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const [cmsOpen, setCmsOpen] = useState(false); // لتوسيع / تصغير الـ CMS

  const user = JSON.parse(localStorage.getItem("user") || "{}");

  const navigationItems = [
    { label: "dashboard", path: "/admin-dashboard", icon: "/images/img_mingcute_home_4_fill.svg" },
    { label: "orders", path: "/order-management", icon: "/images/img_cart.svg" },
    { label: "customers", path: "/customer-management", icon: "/images/img_users.svg" },
    { label: "المشرفين", path: "/admins-management", icon: "/images/img_users.svg" },
    { label: "coupon_management", path: "/coupon-management", icon: "/images/img_ticket.svg" },
    { label: "معلومات الشحن", path: "/shipping-setting", icon: "/images/img_famicons_card_outline.svg" },
    { label: "المحافظات", path: "/governorates-management", icon: "/images/img_ticket.svg" },
    { label: "categories", path: "/category-management", icon: "/images/img_circle_square.svg" },
    { label: "add_product", path: "/add-product", icon: "/images/img_circle_plus.svg" },
    { label: "notifications", path: "/notifications", icon: "/images/img_bell_outline.svg" },
    { label: "product_list", path: "/product-catalog-management", icon: "/images/img_fluent_mdl2_product_list.svg" },
    { label: "عروضات وخصومات", path: "/discounted-product-catalog-management", icon: "/images/img_fluent_mdl2_product_list.svg" },
    { label: "banners_management", path: "/advertisement-management", icon: "/images/img_search.svg" },
  ];

  const cmsItems = [
    { label: "سياسة الخصوصية", path: "/privacy-policy-management" },
    { label: "الشروط والأحكام", path: "/terms" },
    { label: "سياسة الاسترجاع", path: "/return-policy" },
    { label: "من نحن", path: "/about-us" },
    { label: "تواصل معنا", path: "/contact-us" },
  ];

  const handleNavigation = (path, label) => {
    navigate(path);
    setPageTitle(label);
    if (isMobile) onClose();
  };

  const isActiveRoute = (path) => location.pathname === path;

  if (isMobile && !isOpen) return null;

  return (
    <aside
      className={`fixed inset-y-0 left-0 z-50 w-full lg:w-auto h-full bg-white shadow-[0px_3px_4px_#0000001e] lg:shadow-lg pt-5 overflow-y-auto transition-transform duration-300 ease-in-out lg:static lg:inset-auto lg:translate-x-0 ${
        isOpen ? "translate-x-0" : "-translate-x-full"
      } lg:w-[20%] ${isMobile ? "w-full" : ""}`}
      aria-label="Main navigation"
    >
      <div className="flex justify-center items-center w-full px-5 mb-7 sticky top-0 bg-white z-10 py-1 border-b border-gray-200">
        <img src="/images/logo.png" alt={t("logo") || "Logo"} className="w-[180px] h-[100px]" />
        <button onClick={onClose || onToggle} className="w-6 h-6 cursor-pointer hover:opacity-80 lg:hidden">
          <img src="/images/img_material_symbol.svg" alt="Close icon" className="w-6 h-6" />
        </button>
      </div>

      <div className="px-6 mb-4">
        <span className="text-[15px] font-normal text-[#6a717f]">{t("main_menu") || "Main Menu"}</span>
      </div>

      <div className="flex flex-col items-center w-full px-2 flex-1">
        {/* Navigation Items */}
        {navigationItems.map(({ label, path, icon }, index) => {
          const active = isActiveRoute(path);
          return (
            <div
              key={index}
              onClick={() => handleNavigation(path, label)}
              className={`flex justify-start items-center w-full p-3 mb-3 rounded-md cursor-pointer transition-colors ${
                active ? "bg-[#15365F]" : "hover:bg-gray-50"
              }`}
            >
              <img src={icon} className={`w-[22px] h-[22px] ml-2 flex-shrink-0 ${active ? "filter brightness-0 invert" : ""}`} />
              <span className={`text-base ml-2 flex-1 min-w-0 truncate ${active ? "font-bold text-white" : "text-[#6a717f] hover:text-black"}`}>
                {t(label)}
              </span>
            </div>
          );
        })}

        {/* CMS Section */}
        <div
          className="flex justify-start items-center w-full p-3 mb-3 rounded-md cursor-pointer transition-colors hover:bg-gray-50"
          onClick={() => setCmsOpen(!cmsOpen)}
        >
          <img src="/images/img_cms_icon.svg" className="w-[22px] h-[22px] ml-2 flex-shrink-0" />
          <span className="text-base ml-2 flex-1 min-w-0 truncate">{t("CMS") || "CMS"}</span>
          <span className="ml-auto">{cmsOpen ? "▾" : "▸"}</span>
        </div>

        {/* CMS Subitems */}
        {cmsOpen && (
          <div className="flex flex-col w-full pl-10">
            {cmsItems.map(({ label, path }, index) => {
              const active = isActiveRoute(path);
              return (
                <div
                  key={index}
                  onClick={() => handleNavigation(path, label)}
                  className={`flex justify-start items-center w-full p-2 mb-2 rounded-md cursor-pointer transition-colors ${
                    active ? "bg-[#15365F]" : "hover:bg-gray-50"
                  }`}
                >
                  <span className={`text-sm ml-2 flex-1 min-w-0 truncate ${active ? "font-bold text-white" : "text-[#6a717f] hover:text-black"}`}>
                    {label}
                  </span>
                </div>
              );
            })}
          </div>
        )}

        {/* User Profile */}
        <div className="flex justify-start items-center w-full bg-white rounded-lg p-2.5 mb-3 mx-2 mt-auto">
          <div className="flex gap-3 justify-start items-center w-full ml-1.5">
            <img src="/images/img_17_picture.png" alt="User Avatar" className="w-10 h-10 rounded-full flex-shrink-0" />
            <div className="flex flex-col justify-start items-start flex-1 min-w-0">
              <span className="text-sm text-[#737373] truncate">{user?.account || t("user") || "User "}</span>
            </div>
          </div>
          <button
            onClick={() => {
              localStorage.clear();
              navigate("/login");
            }}
            className="w-5 h-5 mr-2 cursor-pointer hover:opacity-80 flex-shrink-0"
          >
            <img src="/images/img_ic_round_logout.svg" alt="Logout icon" />
          </button>
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;